import { CalendarIcon } from '@heroicons/react/24/outline';
import type { SortingState } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { format } from 'date-fns/format';
import { parseISO } from 'date-fns/parseISO';
import { subDays } from 'date-fns/subDays';
import { subMonths } from 'date-fns/subMonths';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type DateRange, DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import { Loading } from '../../components/loading';
import { ReceiptCard } from '../../components/receipt-card';
import { SearchInput } from '../../components/search-input';
import { SortPopover } from '../../components/sort-popover';
import { PageToolbar } from '../../components/toolbar/page-toolbar';
import { ToolbarPopover } from '../../components/toolbar/popover';
import { SearchWithCount } from '../../components/toolbar/search-with-count';
import { compare } from '../../helpers/column-sort';
import { formatDateISO } from '../../helpers/format-date';
import {
  searchPlaceholders,
  useExceptionInvoiceNumbers,
  useReceiptSearch,
  useReceipts,
} from '../../hooks/receipts';
import { useEngagement } from '../../hooks/use-engagement';
import { useSearchFocus } from '../../hooks/use-search-focus';
import { salesJournalRoute, storeLayout } from '../../router';

const NINETY_DAYS = 90;

const defaultSorting: SortingState = [{ id: 'timestamp', desc: true }];

const sortColumnOptions = [
  { id: 'timestamp', label: 'Date' },
  { id: 'total', label: 'Receipt Total' },
];

export function SalesJournalReport() {
  const { from: fromParam, to: toParam, receipt: receiptParam } = salesJournalRoute.useSearch();
  const navigate = salesJournalRoute.useNavigate();

  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const oldestAllowed = subDays(today, NINETY_DAYS);

  const { receipts, loading } = useReceipts();
  const { store } = storeLayout.useRouteContext();
  const timezone = store.timezone;
  const triggerEngagement = useEngagement('sales_journal_engaged', store.id);

  const exceptionInvoiceNumbers = useExceptionInvoiceNumbers();

  const scrolledToReceiptRef = useRef(false);
  const searchReference = useSearchFocus();
  const parentRef = useRef<HTMLDivElement>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [sorting, setSorting] = useState<SortingState>(defaultSorting);

  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    if (!fromParam && !toParam) {
      return undefined;
    }

    return {
      from: fromParam ? parseISO(fromParam) : undefined,
      to: toParam ? parseISO(toParam) : undefined,
    };
  });

  const dateFilteredReceipts = useMemo(() => {
    if (!dateRange?.from) {
      return receipts;
    }

    const rangeEnd = dateRange.to ? new Date(dateRange.to) : undefined;
    rangeEnd?.setHours(23, 59, 59, 999);

    return receipts.filter((r) => {
      if (dateRange.from && r.timestamp < dateRange.from) {
        return false;
      }

      if (rangeEnd && r.timestamp > rangeEnd) {
        return false;
      }

      return true;
    });
  }, [receipts, dateRange]);

  const searchedReceipts = useReceiptSearch(dateFilteredReceipts, searchQuery);

  const sortedReceipts = useMemo(() => {
    const currentSort = sorting[0];
    if (!currentSort) {
      return searchedReceipts;
    }

    const sorted = [...searchedReceipts];
    const { id, desc } = currentSort;
    const direction = desc ? -1 : 1;
    sorted.sort((a, b) => {
      if (id === 'timestamp') {
        return direction * compare(a.timestamp, b.timestamp);
      }

      if (id === 'total') {
        return direction * (a.total - b.total);
      }

      return 0;
    });

    return sorted;
  }, [searchedReceipts, sorting]);

  const virtualizer = useVirtualizer({
    count: sortedReceipts.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => {
      const receipt = sortedReceipts[index];
      if (!receipt) {
        return 110;
      }

      return 110 + (receipt.items?.length ?? 0) * 30;
    },
    overscan: 5,
  });

  const virtualizedReceipts = virtualizer.getVirtualItems();

  // Deep-link: scroll to target receipt (one-shot)
  useEffect(() => {
    if (!receiptParam || scrolledToReceiptRef.current || sortedReceipts.length === 0) {
      return;
    }

    const targetIndex = sortedReceipts.findIndex((r) => String(r.invoice_number) === receiptParam);

    if (targetIndex >= 0) {
      virtualizer.scrollToIndex(targetIndex, { align: 'center' });
      scrolledToReceiptRef.current = true;
    }
  }, [receiptParam, sortedReceipts, virtualizer]);

  const onDateRangeChange = useCallback(
    (range: DateRange | undefined) => {
      triggerEngagement();
      setDateRange(range);
      navigate({
        search: (prev) => ({
          ...prev,
          from: range?.from ? formatDateISO(range.from) : undefined,
          to: range?.to ? formatDateISO(range.to) : undefined,
        }),
        replace: true,
      });
    },
    [navigate, triggerEngagement]
  );

  const onSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      triggerEngagement();
      setSearchQuery(e.target.value);
    },
    [triggerEngagement]
  );

  const dateRangeLabel = (() => {
    if (!dateRange?.from) {
      return 'All dates';
    }

    const fromStr = format(dateRange.from, 'MMM d');
    if (!dateRange.to) {
      return fromStr;
    }

    return `${fromStr} – ${format(dateRange.to, 'MMM d')}`;
  })();

  return (
    <div className="flex h-full flex-col">
      <PageToolbar
        left={
          <div className="flex items-center gap-2">
            <ToolbarPopover
              icon={<CalendarIcon className="size-5" />}
              label={dateRangeLabel}
              minWidth="min-w-fit"
            >
              <DayPicker
                mode="range"
                selected={dateRange}
                onSelect={onDateRangeChange}
                numberOfMonths={2}
                defaultMonth={subMonths(today, 1)}
                disabled={[{ before: oldestAllowed }, { after: today }]}
                style={
                  {
                    '--rdp-accent-color': 'var(--color-emerald-600)',
                    '--rdp-accent-background-color': 'var(--color-emerald-100)',
                  } as React.CSSProperties
                }
              />
              <div className="flex items-center gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => onDateRangeChange(undefined)}
                  className="text-sm text-gray-500 underline hover:text-gray-700"
                >
                  Clear
                </button>
                <div className="flex-1" />
                {[7, 14, 30, 60].map((days) => (
                  <button
                    key={days}
                    type="button"
                    onClick={() =>
                      onDateRangeChange({ from: subDays(new Date(), days), to: new Date() })
                    }
                    className="rounded-sm px-2 py-0.5 text-sm text-gray-600 ring-1 ring-gray-300 hover:bg-gray-50"
                  >
                    Last {days}d
                  </button>
                ))}
              </div>
            </ToolbarPopover>
            <SortPopover
              sorting={sorting}
              onSortingChange={setSorting}
              sortColumnOptions={sortColumnOptions}
            />
          </div>
        }
        right={
          <SearchWithCount
            quantity={sortedReceipts.length}
            total={dateFilteredReceipts.length}
            noun="receipts"
          >
            <SearchInput
              grouped
              inputRef={searchReference}
              placeholders={searchPlaceholders}
              value={searchQuery}
              onChange={onSearchChange}
              shortcut="meta+f"
            />
          </SearchWithCount>
        }
      />

      <div ref={parentRef} className="flex-1 overflow-auto border-t border-gray-200 pt-2">
        {loading ? (
          <Loading />
        ) : (
          <div className="relative px-4" style={{ height: virtualizer.getTotalSize() }}>
            {virtualizedReceipts.map((virtualReceipt) => {
              const receipt = sortedReceipts[virtualReceipt.index];
              if (!receipt) {
                return null;
              }

              return (
                <div
                  key={receipt.invoice_number}
                  data-index={virtualReceipt.index}
                  ref={virtualizer.measureElement}
                  className="absolute left-0 top-0 w-full px-4 py-1.5"
                  style={{ transform: `translateY(${virtualReceipt.start}px)` }}
                >
                  <ReceiptCard
                    receipt={receipt}
                    highlighted={String(receipt.invoice_number) === receiptParam}
                    timezone={timezone}
                    storeId={store.id}
                    hasException={exceptionInvoiceNumbers.has(receipt.invoice_number)}
                  />
                </div>
              );
            })}

            {!loading && sortedReceipts.length === 0 && (
              <div className="py-12 text-center text-sm text-gray-500">
                {searchQuery ? 'No receipts match your search.' : 'No receipts found.'}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
